import { useState } from 'react';
import { featureIsIncludedInCurrentFilter } from '../../helpers/filter';
import { useDataQuery } from '../../hooks/use-data2.hook';
import { ApiConfig, DataOptions, TimeRangeMap, Indicator } from '../../types';
import { FilterFeatures } from '../FilterFeatures';
import { Table } from '../Table';
import { useTitle } from '../../hooks/use-title.hook';
import { useTranslation } from 'react-i18next';
import { getRouteApi, Link } from '@tanstack/react-router';
import { CloseIcon } from '../Icons/CloseIcon';
import { XIcon } from '../Icons/XIcon';
import { useToolStore } from '../Tool/tool.store';
import { FEATURES } from '../../constants';

const routeApi = getRouteApi('/table/');

type Props = {
    config: ApiConfig;
    indicators: Indicator[];
};

export function TablePage(props: Props) {
    const { t } = useTranslation();
    const search = routeApi.useSearch();

    const selectedFeatures = useToolStore((state) => state.selectedFeatures);
    const setSelectedFeatures = useToolStore(
        (state) => state.setSelectedFeatures
    );
    const featureFilter = useToolStore((state) => state.featureFilter);
    const setFeatureFilter = useToolStore((state) => state.setFeatureFilter);
    const shouldZoomToFiltered = useToolStore(
        (state) => state.shouldZoomToFiltered
    );
    const setShouldZoomToFiltered = useToolStore(
        (state) => state.setShouldZoomToFiltered
    );
    const getDataOptions = useToolStore((state) => state.getDataOptions);
    const indicatorsMap = useToolStore((state) => state.indicatorsMap);
    const timeRangeMap = useToolStore((state) => state.timeRangeMap);
    const geoLevel = useToolStore((state) => state.geoLevel);

    const [showOnlySelected, setShowOnlySelected] = useState<boolean>(false);
    const [query, setQuery] = useState<string>('');

    useTitle('Tabell');

    const dataQuery = useDataQuery(getDataOptions(), indicatorsMap);

    if (dataQuery === null) {
        return (
            <div className="fixed inset-0 grid place-items-center">
                <div className="flex flex-col gap-1">
                    Oops! {t('missing-indicator-warning')}
                    <a href="/" className="link-secondary">
                        reset
                    </a>
                </div>
            </div>
        );
    }

    if (!dataQuery.data) return null;

    const { points, options } = dataQuery.data;

    let filteredPoints = points.filter((d) => {
        return featureIsIncludedInCurrentFilter(d, featureFilter);
    });

    if (showOnlySelected && selectedFeatures.length > 0) {
        filteredPoints = filteredPoints.filter((d) => {
            return selectedFeatures.includes(d.properties?.id);
        });
    }

    if (query) {
        filteredPoints = filteredPoints.filter((d) => {
            return (
                d.properties.name.toLowerCase().includes(query.toLowerCase()) ||
                d.properties?.kommunnamn
                    ?.toLowerCase()
                    .includes(query.toLowerCase()) ||
                d.properties?.[options.geoLevel]
                    ?.toLowerCase()
                    .includes(query.toLowerCase())
            );
        });
    }

    const showOnlySelectedDisabled = selectedFeatures.length === 0;

    return (
        <div>
            <div className="flex flex-row gap-12 items-center p-6">
                <Link
                    to="/tool"
                    search={search}
                    className="flex items-center btn btn-sm btn-circle btn-primary"
                    aria-label={t('stang')}
                >
                    <CloseIcon fill="white" />
                </Link>
                <div className="flex flex-row gap-12 items-center justify-center  w-full ">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder={t('search') as string}
                            aria-label={t('search') as string}
                            className="input input-sm input-bordered w-full max-w-sm pr-8"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                            }}
                        />
                        {query && (
                            <button
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                onClick={() => setQuery('')}
                                aria-label={t('rensa')!}
                            >
                                <XIcon />
                            </button>
                        )}
                    </div>

                    {!FEATURES.HIDE_FILTER_FEATURES_BOX && (
                        <div className="flex gap-3">
                            <FilterFeatures
                                featureFilter={featureFilter}
                                onChangeFilter={setFeatureFilter}
                                onToggleZoomToFiltered={() => {
                                    setShouldZoomToFiltered(
                                        !shouldZoomToFiltered
                                    );
                                }}
                                zoomToFiltered={shouldZoomToFiltered}
                                points={points}
                                horizontal
                            />
                        </div>
                    )}
                    <div>
                        <div
                            className={
                                showOnlySelectedDisabled
                                    ? ' tooltip tooltip-left tooltip-warning text-warning-content'
                                    : ''
                            }
                            data-tip={t('inga-punkter-markerade')}
                        >
                            <label
                                className={`flex flex-row gap-2 ${
                                    showOnlySelectedDisabled
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer'
                                } text-sm items-center`}
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        showOnlySelected &&
                                        selectedFeatures.length > 0
                                    }
                                    disabled={showOnlySelectedDisabled}
                                    onChange={() =>
                                        setShowOnlySelected(!showOnlySelected)
                                    }
                                    className="checkbox checkbox-xs checkbox-primary"
                                />
                                {t('show-selected-only')}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <Table
                dataFeatures={filteredPoints}
                dataOptions={options}
                geoLevel={geoLevel}
                timeRangeMap={timeRangeMap}
                selectedFeatures={selectedFeatures}
                featureFilter={featureFilter}
                onSelectFeature={(_selectedFeatures) => {
                    setSelectedFeatures(_selectedFeatures);
                }}
            />
        </div>
    );
}

export default TablePage;
